import React from 'react';
import { motion } from 'framer-motion';
import { CheckCircle, Award, Users, Target, TrendingUp, BookOpen, Shield, Calendar, Lock, Database, FileCheck, ArrowLeft } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { SEO } from '../components/SEO';
import { useNavigate } from 'react-router-dom';
import { UpcomingTrainingsSection } from '../components/UpcomingTrainingsSection';
import { ISOProgressionPath } from '../components/training/ISOProgressionPath';
import { RelatedCoursesSection } from '../components/training/RelatedCoursesSection';
import { standards } from '../data/standards/standardsData';

export const ISO27001LeadAuditor = () => {
  const navigate = useNavigate();

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "Course",
    "name": "ISO 27001 Lead Auditor Course",
    "description": "Professional ISO 27001 Lead Auditor Course for internal, supplier, and certification audits. Globally recognized ISMS auditor training.",
    "provider": {
      "@type": "Organization",
      "name": "ISDE Academy"
    },
    "coursePrerequisites": "Information security or auditing experience recommended",
    "educationalLevel": "Advanced",
    "hasCourseInstance": {
      "@type": "CourseInstance",
      "courseMode": "In-person",
      "duration": "5 days"
    }
  };

  const courseKnowledge = [
    "ISO/IEC 27001:2022 clauses and controls",
    "ISMS implementation and maintenance",
    "Audit principles based on ISO 19011",
    "Leading audit teams and managing audit programs"
  ];

  const keyBenefits = [
    {
      title: "Global Recognition",
      description: "The ISO 27001 lead auditor course is internationally recognized, enabling professionals to work across borders with certification bodies and consulting firms."
    },
    {
      title: "In-Depth Understanding of ISO/IEC 27001:2022",
      description: "Participants gain a detailed understanding of:",
      points: [
        "Context of the organization",
        "Risk assessment and risk treatment",
        "Annex A security controls",
        "Performance evaluation and continual improvement"
      ]
    },
    {
      title: "Advanced Auditing Skills",
      description: "The course develops strong auditing competence in:",
      points: [
        "Audit planning and preparation",
        "Interview techniques and evidence collection",
        "Identifying nonconformities and security gaps",
        "Audit reporting and follow-up actions"
      ]
    },
    {
      title: "Career Growth and Higher Earnings",
      description: "Professionals who complete an ISO 27001 lead auditor course often access higher-paying roles such as lead auditor, ISMS consultant, and compliance manager."
    },
    {
      title: "Improved Organizational Security",
      description: "Internal professionals can strengthen their organization's information security posture by conducting effective internal audits and supporting continual improvement."
    }
  ];

  const targetAudience = [
    "Information Security Managers",
    "IT Managers and System Administrators",
    "Internal and external auditors",
    "ISO 27001 consultants",
    "Risk and compliance professionals",
    "Individuals aspiring to become third-party ISMS auditors"
  ];

  const careerOpportunities = [
    "ISO 27001 Lead Auditor",
    "Information Security Consultant",
    "ISMS Manager",
    "Compliance and Risk Manager",
    "Third-Party Auditor"
  ];

  const industries = [
    "IT & Technology",
    "Healthcare",
    "Finance & Banking",
    "Telecommunications",
    "Government",
    "Manufacturing"
  ];

  const importanceReasons = [
    "Rising global demand for information security auditors",
    "Compliance with data protection laws and regulations",
    "Recognition across industries and countries",
    "Enhanced professional authority in information security"
  ];

  const accreditationBenefits = [
    {
      icon: Shield,
      title: "Globally recognized certification",
      description: "Credentials accepted by certification bodies worldwide"
    },
    {
      icon: BookOpen,
      title: "Training aligned with ISO/IEC 27001:2022",
      description: "Up-to-date content following the latest standard"
    },
    {
      icon: Target,
      title: "Practical, audit-focused learning",
      description: "Hands-on exercises and real audit scenarios"
    },
    {
      icon: Users,
      title: "Qualified trainers with real-world experience",
      description: "Learn from experienced ISMS audit professionals"
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <SEO
        title="ISO 27001 Lead Auditor Course | ISMS Auditor Certification"
        description="Professional ISO 27001 Lead Auditor Course for internal, supplier, and certification audits. Globally recognized ISMS auditor training."
        keywords="ISO 27001 lead auditor, ISMS auditor training, ISO 27001 certification, information security audit, ISO 27001:2022, cybersecurity auditor, ISMS certification"
        canonical="/ISO-27001-Lead-Auditor-Course"
        structuredData={structuredData}
      />

      {/* Hero Section */}
      <div className="bg-gradient-to-r from-purple-600 to-indigo-600 text-white py-20 mt-20">
        <div className="max-w-7xl mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <div className="inline-block bg-purple-500 px-6 py-2 rounded-full text-sm mb-6 font-semibold">
              INFORMATION SECURITY MANAGEMENT SYSTEM CERTIFICATION
            </div>
            <h1 className="text-4xl md:text-6xl font-bold mb-6">
              ISO 27001 Lead Auditor Course
            </h1>
            <p className="text-xl md:text-2xl text-purple-100 mb-8 max-w-4xl mx-auto">
              Professional ISO 27001 Lead Auditor Course for internal, supplier, and certification audits. Globally recognized ISMS auditor training.
            </p>
            <div className="flex flex-wrap justify-center gap-6 text-lg">
              <div className="flex items-center gap-2">
                <Calendar className="h-6 w-6" />
                <span>5 Days Intensive Training</span>
              </div>
              <div className="flex items-center gap-2">
                <Award className="h-6 w-6" />
                <span>Professional Certificate</span>
              </div>
              <div className="flex items-center gap-2">
                <Lock className="h-6 w-6" />
                <span>ISMS Expertise</span>
              </div>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Back Button */}
      <section className="bg-gray-50 py-6">
        <div className="max-w-7xl mx-auto px-4">
          <Button
            onClick={() => navigate('/training/iso-training-services')}
            variant="outline"
            className="flex items-center gap-2 hover:bg-gray-100"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to ISO Training Services
          </Button>
        </div>
      </section>

      {/* ISO Progression Path */}
      <ISOProgressionPath standard={standards['iso-27001']} currentLevel="leadAuditor" />

      {/* Related Courses */}
      <RelatedCoursesSection currentSlug="iso-27001" />

      <div className="max-w-7xl mx-auto px-4 py-12">
        {/* Introduction Section */}
        <section className="mb-16">
          <Card>
            <CardContent className="p-8">
              <p className="text-lg text-gray-700 leading-relaxed mb-6">
                With cyber threats, data breaches, and regulatory requirements increasing worldwide, organizations are prioritizing robust Information Security Management Systems (ISMS). ISO/IEC 27001 is the most recognized international standard for information security, and qualified auditors play a critical role in ensuring compliance and effectiveness. The ISO 27001 Lead Auditor Course is a globally recognized training program designed to equip professionals with the skills required to audit, manage, and improve ISMS in accordance with ISO/IEC 27001:2022.
              </p>
              <p className="text-lg text-gray-700 leading-relaxed">
                Whether you are an information security professional, IT manager, or consultant, enrolling in a lead auditor course can significantly boost your career and professional credibility.
              </p>
            </CardContent>
          </Card>
        </section>

        {/* What Is ISO 27001 Lead Auditor Course */}
        <section className="mb-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-6 text-gray-900">
              What Is an ISO 27001 Lead Auditor Course?
            </h2>
            <Card>
              <CardContent className="p-8">
                <p className="text-lg text-gray-700 mb-6">
                  An ISO 27001 lead auditor course is a professional training program that prepares individuals to plan, conduct, report, and follow up on Information Security Management System audits based on ISO/IEC 27001 requirements.
                </p>
                <div className="mb-6">
                  <h3 className="text-xl font-bold mb-4 text-gray-900">Participants gain comprehensive knowledge of:</h3>
                  <div className="grid md:grid-cols-2 gap-4">
                    {courseKnowledge.map((knowledge, index) => (
                      <div key={index} className="flex items-start gap-3">
                        <CheckCircle className="h-6 w-6 text-purple-600 flex-shrink-0 mt-1" />
                        <span className="text-gray-700">{knowledge}</span>
                      </div>
                    ))}
                  </div>
                </div>
                <p className="text-lg text-gray-700">
                  Successful completion of the ISO 27001 lead auditor course demonstrates your competence as a lead auditor for first-party, second-party, and third-party audits.
                </p>
              </CardContent>
            </Card>
          </motion.div>
        </section>

        {/* Why It's Important */}
        <section className="mb-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h3 className="text-2xl md:text-3xl font-bold mb-6 text-gray-900">
              Why Is ISO 27001 Lead Auditor Course Important?
            </h3>
            <Card className="bg-purple-50 border-purple-200">
              <CardContent className="p-8">
                <p className="text-lg text-gray-700 mb-6">
                  Organizations depend on competent auditors to protect sensitive information, maintain regulatory compliance, and prevent security incidents. The ISO 27001 lead auditor course ensures auditors possess the technical, analytical, and leadership skills necessary to assess ISMS effectiveness.
                </p>
                <h4 className="text-xl font-bold mb-4 text-gray-900">Key reasons to choose this course include:</h4>
                <div className="grid md:grid-cols-2 gap-4">
                  {importanceReasons.map((reason, index) => (
                    <div key={index} className="flex items-start gap-3">
                      <TrendingUp className="h-6 w-6 text-purple-600 flex-shrink-0 mt-1" />
                      <span className="text-gray-700">{reason}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </section>

        {/* Key Benefits */}
        <section className="mb-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h3 className="text-3xl md:text-4xl font-bold mb-8 text-gray-900">
              Key Benefits of ISO 27001 Lead Auditor Course
            </h3>
            <div className="grid md:grid-cols-2 gap-6">
              {keyBenefits.map((benefit, index) => (
                <Card key={index} className="hover:shadow-lg transition-shadow">
                  <CardContent className="p-6">
                    <div className="flex items-start gap-4">
                      <div className="bg-purple-100 p-3 rounded-lg flex-shrink-0">
                        <Database className="h-6 w-6 text-purple-600" />
                      </div>
                      <div className="flex-1">
                        <h4 className="text-xl font-bold mb-3 text-gray-900">{index + 1}. {benefit.title}</h4>
                        <p className="text-gray-700 mb-3">{benefit.description}</p>
                        {benefit.points && (
                          <ul className="space-y-2">
                            {benefit.points.map((point, idx) => (
                              <li key={idx} className="flex items-start gap-2">
                                <span className="text-purple-600 mt-1">•</span>
                                <span className="text-gray-700">{point}</span>
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </motion.div>
        </section>

        {/* Who Should Attend */}
        <section className="mb-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h4 className="text-2xl md:text-3xl font-bold mb-6 text-gray-900 flex items-center gap-3">
              <Users className="h-8 w-8 text-purple-600" />
              Who Should Attend ISO 27001 Lead Auditor Course?
            </h4>
            <Card>
              <CardContent className="p-8">
                <p className="text-lg text-gray-700 mb-6">The ISO 27001 lead auditor course is ideal for:</p>
                <div className="grid md:grid-cols-2 gap-4">
                  {targetAudience.map((audience, index) => (
                    <div key={index} className="flex items-start gap-3">
                      <CheckCircle className="h-6 w-6 text-purple-600 flex-shrink-0 mt-1" />
                      <span className="text-gray-700">{audience}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </section>

        {/* Career Opportunities */}
        <section className="mb-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h4 className="text-2xl md:text-3xl font-bold mb-6 text-gray-900">
              Career Opportunities After ISO 27001 Lead Auditor Course
            </h4>
            <Card className="bg-gradient-to-br from-purple-50 to-indigo-50 border-purple-200">
              <CardContent className="p-8">
                <p className="text-lg text-gray-700 mb-6">
                  Professionals completing the course can pursue roles such as:
                </p>
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
                  {careerOpportunities.map((opportunity, index) => (
                    <div key={index} className="flex items-start gap-3 bg-white p-4 rounded-lg shadow-sm">
                      <Award className="h-6 w-6 text-purple-600 flex-shrink-0 mt-1" />
                      <span className="text-gray-700 font-semibold">{opportunity}</span>
                    </div>
                  ))}
                </div>
                <div className="border-t border-purple-200 pt-6">
                  <h5 className="text-lg font-bold text-gray-900 mb-4">Industries include:</h5>
                  <div className="flex flex-wrap gap-3">
                    {industries.map((industry, index) => (
                      <div key={index} className="bg-white px-4 py-2 rounded-full border border-purple-200 text-gray-700 font-medium">
                        {industry}
                      </div>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </section>

        {/* Why Choose Accredited Training */}
        <section className="mb-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h4 className="text-2xl md:text-3xl font-bold mb-6 text-gray-900">
              Why Choose an Accredited ISO 27001 Lead Auditor Course?
            </h4>
            <Card>
              <CardContent className="p-8">
                <p className="text-lg text-gray-700 mb-6">
                  Selecting an accredited ISO 27001 lead auditor course ensures:
                </p>
                <div className="grid md:grid-cols-2 gap-6 mb-6">
                  {accreditationBenefits.map((benefit, index) => {
                    const IconComponent = benefit.icon;
                    return (
                      <div key={index} className="flex items-start gap-4">
                        <div className="bg-purple-100 p-3 rounded-lg flex-shrink-0">
                          <IconComponent className="h-6 w-6 text-purple-600" />
                        </div>
                        <div>
                          <h5 className="font-bold text-gray-900 mb-2">{benefit.title}</h5>
                          <p className="text-gray-700">{benefit.description}</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
                <p className="text-lg text-gray-700 font-semibold">
                  Accredited training prepares professionals to handle complex ISMS audits confidently.
                </p>
              </CardContent>
            </Card>
          </motion.div>
        </section>

        {/* CTA Section */}
        <section>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <Card className="bg-gradient-to-r from-purple-600 to-indigo-600 text-white">
              <CardContent className="p-12 text-center">
                <h2 className="text-3xl md:text-4xl font-bold mb-6">
                  Ready to Become a Certified ISO 27001 Lead Auditor?
                </h2>
                <p className="text-xl text-purple-100 mb-8 max-w-3xl mx-auto">
                  Join thousands of professionals who have enhanced their information security careers with our internationally recognized certification program.
                </p>
                <div className="flex flex-wrap justify-center gap-4">
                  <Button
                    onClick={() => navigate('/training-calendar')}
                    className="bg-white text-purple-600 hover:bg-gray-100 px-8 py-6 text-lg"
                    size="lg"
                  >
                    <Calendar className="h-5 w-5 mr-2" />
                    View Training Schedule
                  </Button>
                  <Button
                    onClick={() => navigate('/contact')}
                    variant="outline"
                    className="border-2 border-white text-white hover:bg-white hover:text-purple-600 px-8 py-6 text-lg"
                    size="lg"
                  >
                    Request More Information
                  </Button>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </section>
      </div>

      {/* Upcoming Trainings Section */}
      <UpcomingTrainingsSection
        pageSlug="iso-27001-lead-auditor"
        title="Upcoming ISO 27001 Lead Auditor Training Sessions"
      />
    </div>
  );
};
